/** Apply only an editor's changes to the latest committed snapshot. Independent queued edits survive. */
export function rebaseValue(before: unknown, edited: unknown, current: unknown): unknown {
  if (JSON.stringify(before) === JSON.stringify(edited)) return current;
  if (Array.isArray(before) && Array.isArray(edited) && Array.isArray(current)) {
    const rows = [...before, ...edited, ...current];
    const hasIds = rows.every((v) => v && typeof v === 'object' && 'id' in v);
    const hasFollowKeys = rows.every(
      (v) =>
        v &&
        typeof v === 'object' &&
        typeof v.follower_id === 'string' &&
        typeof v.following_id === 'string',
    );
    if (hasIds || hasFollowKeys) {
      const key = (v: Record<string, any>) =>
        hasIds ? v.id : `${v.follower_id}:${v.following_id}`;
      const old = new Map(before.map((v) => [key(v), v])),
        next = new Map(edited.map((v) => [key(v), v]));
      const result = current
        .filter((v) => !old.has(key(v)) || next.has(key(v)))
        .map((v) =>
          next.has(key(v)) && old.has(key(v))
            ? rebaseValue(old.get(key(v)), next.get(key(v)), v)
            : v,
        );
      for (const row of edited)
        if (!old.has(key(row)) && !result.some((v) => key(v) === key(row))) result.push(row);
      return result;
    }
    if ([...before, ...edited, ...current].every((v) => typeof v === 'string')) {
      const removed = new Set(before.filter((v) => !edited.includes(v)));
      return [
        ...new Set([
          ...current.filter((v) => !removed.has(v)),
          ...edited.filter((v) => !before.includes(v)),
        ]),
      ];
    }
    return edited;
  }
  if (
    before &&
    edited &&
    current &&
    typeof before === 'object' &&
    typeof edited === 'object' &&
    typeof current === 'object' &&
    !Array.isArray(edited)
  ) {
    const b = before as Record<string, unknown>,
      e = edited as Record<string, unknown>,
      c = current as Record<string, unknown>,
      result = { ...c };
    for (const key of new Set([...Object.keys(b), ...Object.keys(e)])) {
      if (!(key in e)) {
        if (key in b) delete result[key];
      } else if (JSON.stringify(b[key]) !== JSON.stringify(e[key]))
        result[key] = rebaseValue(b[key], e[key], c[key]);
    }
    return result;
  }
  return edited;
}
